/**
 * In-app support chat. The customer is already authenticated, so the gateway
 * takes their identity (name, email, clinic, role) from the JWT; the client
 * sends only the text, the page they're on and lightweight context.
 *
 * Each conversation is a thread in the team's Slack support channel, and the
 * team's replies arrive over the support WebSocket (see stores/support.ts).
 */
import { apiClient } from './client'

export interface SupportMessage {
  id: number
  sender: 'customer' | 'staff'
  author_name: string | null
  body: string
  created_at: string
}

export interface SupportConversationSummary {
  id: string
  created_at: string
  last_message_at: string
  unread: number
  preview: string
}

export interface SupportConversation {
  id: string
  created_at?: string
  last_message_at?: string
  unread: number
  messages: SupportMessage[]
}

export interface StartConversationPayload {
  message: string
  page?: string
  url?: string
  context?: Record<string, string | number | null | undefined>
}

export function listConversations(): Promise<SupportConversationSummary[]> {
  return apiClient('/support/conversations')
}

export function getConversation(id: string): Promise<SupportConversation> {
  return apiClient(`/support/conversations/${encodeURIComponent(id)}`)
}

export function startConversation(payload: StartConversationPayload): Promise<SupportConversation> {
  return apiClient('/support/conversations', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export function sendMessage(id: string, message: string): Promise<SupportMessage> {
  return apiClient(`/support/conversations/${encodeURIComponent(id)}/messages`, {
    method: 'POST',
    body: JSON.stringify({ message }),
  })
}

export function markRead(id: string): Promise<{ status: string }> {
  return apiClient(`/support/conversations/${encodeURIComponent(id)}/read`, { method: 'POST' })
}

export function getUnread(): Promise<{ unread: number }> {
  return apiClient('/support/unread')
}
